import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import {
  BarChart3,
  Check,
  Copy,
  Download,
  KeyRound,
  Receipt,
  Trash2,
} from 'lucide-react'
import { toast } from 'sonner'
import { AppHeader } from '#/components/layout/app-header'
import { AiConnectionsCard } from '#/components/byoa/connections'
import { MachinesCard } from '#/components/serve/machines-card'
import { DiscordCta } from '#/components/guided/discord-cta'
import { Badge } from '#/components/ui/badge'
import { Button } from '#/components/ui/button'
import { Input } from '#/components/ui/input'
import { Label } from '#/components/ui/label'
import { Progress } from '#/components/ui/progress'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '#/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '#/components/ui/dialog'
import { Textarea } from '#/components/ui/textarea'
import { Checkbox } from '#/components/ui/checkbox'
import { Switch } from '#/components/ui/switch'
import { setBuilderMode, useBuilderMode } from '#/lib/simple-mode'
import { useCopy } from '#/components/common/prompt-block'
import { ModelPicker } from '#/components/common/model-picker'
import type { ModelId } from '#/lib/ai/models'
import { Markdown } from '#/components/common/markdown'
import { api, ApiError, useSubscription } from '#/lib/api'
import {
  CANCEL_REASONS,
  useCancelSubscription,
  useSubscriptionHistory,
} from '#/lib/api-billing'
import type { CancelReason, SubscriptionStatusExt } from '#/lib/api-billing'
import {
  NOTIFY_EVENTS,
  useAddNotificationChannel,
  useNotificationChannels,
  useProfile,
  useRemoveNotificationChannel,
  useSetDigestHour,
  useUpdateProfile,
} from '#/lib/api-flywheel'
import type { NotifyChannelKind, NotifyEvent } from '#/lib/api-flywheel'
import { authClient } from '#/lib/auth-client'
import { fmtDate } from '#/lib/format'
import { cn } from '#/lib/utils'
import { m } from '#/paraglide/messages'
import { setLocale } from '#/paraglide/runtime'

export const Route = createFileRoute('/settings')({ component: Settings })

function Card({
  title,
  desc,
  badge,
  children,
}: {
  title: string
  desc?: string
  badge?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className="space-y-4 rounded-2xl border bg-card p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-semibold">{title}</h2>
          {desc ? (
            <p className="text-sm text-muted-foreground">{desc}</p>
          ) : null}
        </div>
        {badge}
      </div>
      {children}
    </section>
  )
}

function UsageBar({
  label,
  used,
  limit,
}: {
  label: string
  used: number
  limit: number
}) {
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span>{label}</span>
        <span className="tabular-nums text-muted-foreground">
          {limit === -1 ? `${used} / ∞` : `${used} / ${limit}`}
        </span>
      </div>
      <Progress
        value={
          limit === -1 ? 5 : limit ? Math.min(100, (used / limit) * 100) : 0
        }
        className="h-1.5"
      />
    </div>
  )
}

const reasonLabel = (r: CancelReason) =>
  r === 'too_expensive'
    ? m.settings_cancel_reason_too_expensive()
    : r === 'missing_features'
      ? m.settings_cancel_reason_missing_features()
      : r === 'rarely_used'
        ? m.settings_cancel_reason_rarely_used()
        : r === 'found_alternative'
          ? m.settings_cancel_reason_found_alternative()
          : r === 'just_trying'
            ? m.settings_cancel_reason_just_trying()
            : m.settings_cancel_reason_other()

/** Churn survey (spec/01 §9.4): six reasons + optional note → POST /api/subscription/cancel. */
function CancelDialog({
  open,
  onOpenChange,
  expiresAt,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  expiresAt: string
}) {
  const [reason, setReason] = useState<CancelReason | null>(null)
  const [note, setNote] = useState('')
  const cancel = useCancelSubscription()
  const submit = async () => {
    if (!reason) return
    try {
      const r = await cancel.mutateAsync({ reason, note: note || undefined })
      toast.success(m.settings_cancel_done({ date: fmtDate(r.expiresAt) }))
      onOpenChange(false)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : m.common_error_generic())
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{m.settings_cancel_title()}</DialogTitle>
          <DialogDescription>
            {m.settings_cancel_desc({ date: fmtDate(expiresAt) })}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2" role="radiogroup">
          {CANCEL_REASONS.map((r) => (
            <button
              key={r}
              type="button"
              role="radio"
              aria-checked={reason === r}
              onClick={() => setReason(r)}
              className={cn(
                'flex items-center gap-3 rounded-lg border px-3 py-2 text-left text-sm transition-colors hover:bg-muted',
                reason === r && 'border-primary bg-primary/5',
              )}
            >
              <span
                className={cn(
                  'size-3.5 shrink-0 rounded-full border-2',
                  reason === r
                    ? 'border-primary bg-primary'
                    : 'border-muted-foreground/50',
                )}
              />
              {reasonLabel(r)}
            </button>
          ))}
        </div>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={m.settings_cancel_note()}
          rows={3}
          maxLength={1000}
        />
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {m.settings_cancel_keep()}
          </Button>
          <Button
            variant="destructive"
            disabled={!reason || cancel.isPending}
            onClick={submit}
          >
            {m.settings_cancel_confirm()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const eventLabel = (e: NotifyEvent) =>
  e === 'checkpoint'
    ? m.fw_notify_ev_checkpoint()
    : e === 'done'
      ? m.fw_notify_ev_done()
      : e === 'stuck'
        ? m.fw_notify_ev_stuck()
        : e === 'room'
          ? m.fw_notify_ev_room()
          : e === 'inbox'
            ? m.fw_notify_ev_inbox()
            : m.fw_notify_ev_comment()

const DIGEST_HOURS = [6, 7, 8, 9, 12, 17, 18, 20, 21]

/** Notification channels (moat plan §7): where checkpoint / done / stuck / comment events land. */
function NotificationsCard({ enabled }: { enabled: boolean }) {
  const { data } = useNotificationChannels(enabled)
  const add = useAddNotificationChannel()
  const remove = useRemoveNotificationChannel()
  const setDigest = useSetDigestHour()
  const [kind, setKind] = useState<NotifyChannelKind>('email')
  const [target, setTarget] = useState('')
  const [events, setEvents] = useState<NotifyEvent[]>([
    'checkpoint',
    'done',
    'stuck',
  ])
  const toggle = (e: NotifyEvent) =>
    setEvents((prev) =>
      prev.includes(e) ? prev.filter((x) => x !== e) : [...prev, e],
    )
  const submit = async () => {
    if (!target.trim()) return
    if (!events.length) return toast.error(m.fw_notify_events_required())
    try {
      await add.mutateAsync({ kind, target: target.trim(), events })
      setTarget('')
      toast.success(m.settings_saved())
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : m.common_error_generic())
    }
  }
  const providers = data?.providers
  return (
    <Card title={m.fw_notify_title()} desc={m.fw_notify_desc()}>
      {data?.discordInviteUrl ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/30 p-3">
          <p className="text-sm text-muted-foreground">{m.gd_discord_desc()}</p>
          <DiscordCta url={data.discordInviteUrl} compact />
        </div>
      ) : null}
      {providers && !providers.email ? (
        <p className="rounded-lg border border-status-progress/40 bg-status-progress/5 p-2.5 text-xs text-status-progress">
          {m.fw_notify_email_off()}
        </p>
      ) : null}
      {providers && !providers.telegram ? (
        <p className="rounded-lg border border-status-progress/40 bg-status-progress/5 p-2.5 text-xs text-status-progress">
          {m.fw_notify_telegram_off()}
        </p>
      ) : null}
      {data?.channels.length ? (
        <ul className="divide-y text-sm">
          {data.channels.map((c) => (
            <li key={c.id} className="flex items-center gap-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="capitalize">
                    {c.kind === 'telegram'
                      ? m.fw_notify_telegram()
                      : c.kind === 'discord'
                        ? m.gd_notify_discord()
                        : m.fw_notify_email()}
                  </Badge>
                  <span className="truncate font-medium">{c.target}</span>
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {c.events.map((e) => (
                    <span
                      key={e}
                      className="rounded-full bg-muted px-1.5 py-px text-[10px] font-medium text-muted-foreground"
                    >
                      {eventLabel(e as NotifyEvent)}
                    </span>
                  ))}
                </div>
                {/* Inspirasi F5 — daily digest hour (Fractal "routines": a scheduled ritual, owned by the channel) */}
                <label className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{m.fw_notify_digest()}</span>
                  <select
                    className="h-7 rounded-md border bg-background px-1.5 text-xs"
                    value={c.digestHour == null ? 'off' : String(c.digestHour)}
                    aria-label={m.fw_notify_digest()}
                    onChange={(e) =>
                      setDigest.mutate({
                        id: c.id,
                        digestHour: e.target.value === 'off' ? null : Number(e.target.value),
                      })
                    }
                  >
                    <option value="off">{m.fw_notify_digest_off()}</option>
                    {DIGEST_HOURS.map((h) => (
                      <option key={h} value={String(h)}>
                        {m.fw_notify_digest_at({ hour: String(h).padStart(2, '0') })}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <Button
                size="icon"
                variant="ghost"
                className="size-8"
                aria-label={m.common_delete()}
                onClick={() => remove.mutate(c.id)}
              >
                <Trash2 className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">{m.fw_notify_empty()}</p>
      )}
      <div className="grid gap-3 rounded-xl border bg-muted/30 p-3 sm:grid-cols-[9rem_1fr]">
        <div className="space-y-1">
          <Label>{m.fw_notify_kind()}</Label>
          <Select
            value={kind}
            onValueChange={(v) => setKind(v as NotifyChannelKind)}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="email">{m.fw_notify_email()}</SelectItem>
              <SelectItem value="telegram">{m.fw_notify_telegram()}</SelectItem>
              <SelectItem value="discord">{m.gd_notify_discord()}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>{m.fw_notify_target()}</Label>
          <Input
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder={
              kind === 'email'
                ? m.fw_notify_target_email_ph()
                : kind === 'discord'
                  ? m.gd_notify_target_discord_ph()
                  : m.fw_notify_target_telegram_ph()
            }
          />
          {kind === 'discord' ? (
            <p className="text-xs text-muted-foreground">
              {m.gd_notify_discord_hint()}
            </p>
          ) : null}
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label>{m.fw_notify_events()}</Label>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {NOTIFY_EVENTS.map((e) => (
              <label key={e} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={events.includes(e)}
                  onCheckedChange={() => toggle(e)}
                />{' '}
                {eventLabel(e)}
              </label>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">{m.fw_notify_digest_hint()}</p>
        </div>
        <div className="sm:col-span-2">
          <Button
            size="sm"
            disabled={!target.trim() || add.isPending}
            onClick={submit}
          >
            {m.fw_notify_add()}
          </Button>
        </div>
      </div>
    </Card>
  )
}

/** Simple mode (non-tech F0): plain language, no technical choices. Additive — `dev` stays the default. */
function SimpleModeCard() {
  const { simple } = useBuilderMode()
  const [busy, setBusy] = useState(false)
  const set = async (v: boolean) => {
    setBusy(true)
    try {
      await setBuilderMode(v ? 'simple' : 'dev')
      toast.success(m.simple_settings_saved())
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : m.common_error_generic())
    } finally {
      setBusy(false)
    }
  }
  return (
    <Card title={m.simple_settings_title()} desc={m.simple_settings_desc()}>
      <div className="flex items-center gap-3">
        <Switch id="simple-mode" checked={simple} onCheckedChange={set} disabled={busy} aria-label={m.simple_settings_toggle()} data-testid="simple-mode-toggle" />
        <Label htmlFor="simple-mode" className="font-normal">
          {m.simple_settings_toggle()}
        </Label>
      </div>
    </Card>
  )
}

/** Public benchmark opt-out (moat plan §7). Opting out removes the user from the query, not from a filter. */
function BenchmarkCard({ enabled }: { enabled: boolean }) {
  const { data } = useProfile(enabled)
  const update = useUpdateProfile()
  const optOut = data?.user.benchmarkOptOut ?? false
  const set = async (v: boolean) => {
    try {
      await update.mutateAsync({ benchmarkOptOut: v })
      toast.success(m.fw_bench_saved())
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : m.common_error_generic())
    }
  }
  return (
    <Card title={m.fw_bench_title()} desc={m.fw_bench_desc()}>
      <div className="flex items-center gap-3">
        <Switch
          id="bench-optout"
          checked={optOut}
          onCheckedChange={set}
          disabled={update.isPending}
          aria-label={m.fw_bench_optout()}
        />
        <Label htmlFor="bench-optout" className="font-normal">
          {m.fw_bench_optout()}
        </Label>
      </div>
      <Button asChild variant="ghost" size="sm">
        <Link to="/benchmark">
          <BarChart3 className="size-4" /> {m.fw_bench_link()}
        </Link>
      </Button>
    </Card>
  )
}

/** Default AI model (docs/PLAN_MODEL_SELECTOR.md §2.5) — stored in user.preferred_model. */
function ModelCard({ sub }: { sub: SubscriptionStatusExt | undefined }) {
  const { data: profile } = useProfile()
  const update = useUpdateProfile()
  const value = ((
    profile?.user as { preferredModel?: string | null } | undefined
  )?.preferredModel ?? null) as ModelId | null
  const set = async (id: ModelId | null) => {
    try {
      await update.mutateAsync({ preferredModel: id } as never)
      toast.success(m.model_saved())
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : m.common_error_generic())
    }
  }
  const premium = sub?.models?.premium
  return (
    <Card title={m.model_title()} desc={m.model_desc()}>
      <ModelPicker value={value} onChange={set} allowDefault size="md" />
      {premium && premium.limit !== 0 ? (
        premium.limit === -1 ? (
          <p className="text-xs text-muted-foreground">
            {m.model_premium_pool_unlimited()}
          </p>
        ) : (
          <UsageBar
            label={m.model_badge_premium()}
            used={Math.round(premium.used * 10) / 10}
            limit={premium.limit}
          />
        )
      ) : null}
    </Card>
  )
}

/** Audit log export per workspace (moat plan §7, EU AI Act record). */
function AuditCard({
  workspaces,
}: {
  workspaces: { id: string; name: string }[]
}) {
  return (
    <Card title={m.fw_audit_title()} desc={m.fw_audit_desc()}>
      {workspaces.length ? (
        <ul className="divide-y text-sm">
          {workspaces.map((w) => (
            <li key={w.id} className="flex items-center gap-3 py-2">
              <span className="min-w-0 flex-1 truncate">{w.name}</span>
              <Button size="sm" variant="ghost" className="h-7" asChild>
                <a href={`/api/workspaces/${w.id}/audit?format=csv`} download>
                  <Download className="size-3.5" /> {m.fw_audit_csv()}
                </a>
              </Button>
              <Button size="sm" variant="ghost" className="h-7" asChild>
                <a
                  href={`/api/workspaces/${w.id}/audit?format=json`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {m.fw_audit_json()}
                </a>
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">{m.fw_audit_empty()}</p>
      )}
    </Card>
  )
}

function Settings() {
  const { data: session, isPending } = authClient.useSession()
  const navigate = useNavigate()
  const qc = useQueryClient()
  useEffect(() => {
    if (!isPending && !session?.user)
      navigate({ to: '/sign-in', search: { redirect: '/settings' } })
  }, [isPending, session, navigate])
  const { data: subRaw } = useSubscription(Boolean(session?.user))
  const sub = subRaw as SubscriptionStatusExt | undefined
  const { data: hist } = useSubscriptionHistory(Boolean(session?.user))
  const { data: tokens } = useQuery({
    queryKey: ['tokens'],
    queryFn: () =>
      api<{
        tokens: {
          id: string
          name: string
          prefix: string
          kind: string
          createdAt: string
          lastUsedAt: string | null
        }[]
      }>('/api/account/tokens'),
    enabled: Boolean(session?.user),
  })
  const { data: wss } = useQuery({
    queryKey: ['workspaces'],
    queryFn: () =>
      api<{ workspaces: { id: string; name: string }[] }>('/api/workspaces'),
    enabled: Boolean(session?.user),
  })
  const [name, setName] = useState('')
  const [image, setImage] = useState('')
  const [locale, setLoc] = useState<'en' | 'id'>('en')
  useEffect(() => {
    if (session?.user) {
      setName(session.user.name ?? '')
      setImage(session.user.image ?? '')
      setLoc(
        (session.user as { locale?: string }).locale === 'id' ? 'id' : 'en',
      )
    }
  }, [session])
  const [keyName, setKeyName] = useState('')
  const [newKey, setNewKey] = useState<string | null>(null)
  const { copied, copy } = useCopy()
  const [cancelOpen, setCancelOpen] = useState(false)
  const planLabel = sub?.planType?.replace(/-usd$/, '') ?? 'free'
  if (!session?.user) return null

  const saveProfile = async () => {
    const r = await authClient.updateUser({
      name,
      image: image || undefined,
      locale,
    })
    if (r.error) return toast.error(r.error.message ?? m.common_error_generic())
    setLocale(locale)
    toast.success(m.settings_saved())
  }
  const genKey = async () => {
    try {
      const r = await api<{ token: string }>('/api/account/tokens', {
        method: 'POST',
        json: { name: keyName || 'CLI' },
      })
      setNewKey(r.token)
      setKeyName('')
      qc.invalidateQueries({ queryKey: ['tokens'] })
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : m.common_error_generic())
    }
  }
  const delKey = async (id: string) => {
    await api(`/api/account/tokens/${id}`, { method: 'DELETE' })
    qc.invalidateQueries({ queryKey: ['tokens'] })
  }

  return (
    <>
      <AppHeader />
      <main className="flex-1 overflow-y-auto px-6 py-10">
        <div className="mx-auto w-full max-w-2xl space-y-5">
          <h1 className="text-3xl font-bold">{m.settings_title()}</h1>
          <Card
            title={m.settings_usage_title()}
            desc={m.settings_usage_desc()}
            badge={<Badge className="capitalize">{planLabel}</Badge>}
          >
            {sub ? (
              <div className="space-y-3">
                <UsageBar
                  label={m.settings_usage_prd()}
                  used={sub.prdUsage.used}
                  limit={sub.prdUsage.limit}
                />
                <UsageBar
                  label={m.settings_usage_chat()}
                  used={sub.chatUsage.used}
                  limit={sub.chatUsage.limit}
                />
                {sub.subscription ? (
                  <p className="text-xs text-muted-foreground">
                    {m.settings_usage_active_until({
                      date: fmtDate(sub.subscription.expiresAt),
                    })}
                  </p>
                ) : null}
              </div>
            ) : null}
          </Card>
          <ModelCard sub={sub} />
          <Card
            title={m.settings_history_title()}
            desc={m.settings_history_desc()}
          >
            {hist?.subscriptions.length ? (
              <ul className="divide-y text-sm">
                {hist.subscriptions.map((s) => (
                  <li
                    key={s.id}
                    className="flex items-center justify-between py-2"
                  >
                    <span>
                      {s.planName}{' '}
                      <span className="text-muted-foreground">
                        · {fmtDate(s.startsAt)} – {fmtDate(s.expiresAt)}
                      </span>
                    </span>
                    <Badge
                      variant={
                        s.state === 'active'
                          ? 'default'
                          : s.state === 'cancelled'
                            ? 'outline'
                            : 'secondary'
                      }
                    >
                      {s.state === 'active'
                        ? m.settings_history_active()
                        : s.state === 'scheduled'
                          ? m.settings_history_scheduled()
                          : s.state === 'cancelled'
                            ? m.settings_history_cancelled()
                            : m.settings_history_expired()}
                    </Badge>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                {m.settings_history_empty()}
              </p>
            )}
          </Card>
          <Card
            title={m.settings_sub_title()}
            badge={
              <Badge
                variant={sub?.cancelAtPeriodEnd ? 'outline' : 'secondary'}
                className="capitalize"
              >
                {sub?.cancelAtPeriodEnd
                  ? m.settings_history_cancelled()
                  : planLabel}
              </Badge>
            }
          >
            <div className="space-y-1 text-sm">
              {sub?.subscription ? (
                <>
                  <p>
                    {m.settings_sub_active_until({
                      plan: sub.subscription.plan.name,
                      date: fmtDate(sub.subscription.expiresAt),
                    })}
                  </p>
                  {sub.cancelAtPeriodEnd ? (
                    <p className="text-status-failed">
                      {m.settings_sub_cancel_scheduled({
                        date: fmtDate(sub.subscription.expiresAt),
                      })}
                    </p>
                  ) : null}
                  {sub.nextSubscription ? (
                    <p className="text-muted-foreground">
                      {m.settings_sub_next({
                        plan: sub.nextSubscription.plan.name,
                        date: fmtDate(sub.nextSubscription.startsAt),
                      })}
                    </p>
                  ) : null}
                </>
              ) : (
                <p className="text-muted-foreground">{m.settings_sub_none()}</p>
              )}
              {sub?.addonCredits.hasHistory ? (
                <p className="text-muted-foreground">
                  {m.settings_sub_credits({
                    prd: sub.addonCredits.prdCredits,
                    chat: sub.addonCredits.chatCredits,
                  })}
                </p>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="secondary">
                <Link to="/pricing">{m.settings_upgrade_cta()}</Link>
              </Button>
              <Button asChild variant="ghost">
                <Link to="/transactions">
                  <Receipt className="size-4" /> {m.settings_sub_transactions()}
                </Link>
              </Button>
              {sub?.subscription && !sub.cancelAtPeriodEnd ? (
                <Button
                  variant="ghost"
                  className="text-status-failed hover:text-status-failed"
                  onClick={() => setCancelOpen(true)}
                >
                  {m.settings_sub_cancel()}
                </Button>
              ) : null}
            </div>
            {sub?.subscription ? (
              <CancelDialog
                open={cancelOpen}
                onOpenChange={setCancelOpen}
                expiresAt={sub.subscription.expiresAt}
              />
            ) : null}
          </Card>
          <Card
            title={m.settings_profile_title()}
            desc={m.settings_profile_desc()}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>{m.settings_profile_name()}</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>{m.settings_profile_email()}</Label>
                <Input value={session.user.email} disabled />
                <p className="text-[11px] text-muted-foreground">
                  {m.settings_profile_email_hint()}
                </p>
              </div>
              <div className="space-y-1">
                <Label>{m.settings_profile_avatar()}</Label>
                <Input
                  value={image}
                  onChange={(e) => setImage(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label>{m.settings_profile_locale()}</Label>
                <Select
                  value={locale}
                  onValueChange={(v) => setLoc(v as 'en' | 'id')}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="id">Bahasa Indonesia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={saveProfile}>{m.common_save()}</Button>
          </Card>
          <Card title={m.settings_keys_title()}>
            <Markdown
              content={m.settings_keys_desc()}
              className="text-sm text-muted-foreground"
            />
            <div className="flex gap-2">
              <Input
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
                placeholder={m.settings_keys_placeholder()}
              />
              <Button onClick={genKey}>
                <KeyRound className="size-4" /> {m.settings_keys_generate()}
              </Button>
            </div>
            {newKey ? (
              <div className="space-y-2 rounded-xl border border-primary/50 bg-primary/5 p-3">
                <div className="text-sm font-medium">
                  {m.settings_keys_new_title()}
                </div>
                <p className="text-xs text-muted-foreground">
                  {m.settings_keys_new_desc()}
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 truncate rounded bg-muted px-2 py-1 font-mono text-xs">
                    {newKey}
                  </code>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => copy(newKey)}
                  >
                    {copied ? (
                      <Check className="size-4" />
                    ) : (
                      <Copy className="size-4" />
                    )}
                  </Button>
                </div>
              </div>
            ) : null}
            {tokens?.tokens.length ? (
              <ul className="divide-y text-sm">
                {tokens.tokens.map((t) => (
                  <li key={t.id} className="flex items-center gap-3 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium">{t.name}</span>
                        <Badge variant="outline" className="uppercase">
                          {t.kind}
                        </Badge>
                        <code className="font-mono text-xs text-muted-foreground">
                          {t.prefix}
                        </code>
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {m.settings_keys_created({
                          date: fmtDate(t.createdAt),
                        })}{' '}
                        ·{' '}
                        {t.lastUsedAt
                          ? m.settings_keys_last_used({
                              date: fmtDate(t.lastUsedAt),
                            })
                          : m.settings_keys_never_used()}
                      </div>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="size-8"
                      onClick={() => delKey(t.id)}
                      aria-label={m.common_delete()}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                {m.settings_keys_empty()}
              </p>
            )}
          </Card>
          <NotificationsCard enabled={Boolean(session?.user)} />
          <SimpleModeCard />
          <AiConnectionsCard enabled={Boolean(session?.user)} />
          {/* Parity P0 — computers connected with `bystep serve` */}
          <MachinesCard enabled={Boolean(session?.user)} />
          <BenchmarkCard enabled={Boolean(session?.user)} />
          <AuditCard workspaces={wss?.workspaces ?? []} />
          <Card title={m.settings_ws_title()}>
            <p className="text-sm text-muted-foreground">
              {m.settings_ws_desc()}{' '}
              <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                bystep connect --workspace &lt;id&gt;
              </code>
            </p>
            <ul className="divide-y text-sm">
              {(wss?.workspaces ?? []).map((w) => (
                <li key={w.id} className="flex items-center gap-3 py-2">
                  <span className="min-w-0 flex-1 truncate">{w.name}</span>
                  <code className="font-mono text-[11px] text-muted-foreground">
                    {w.id}
                  </code>
                  <Button size="sm" variant="ghost" className="h-7" asChild>
                    <Link
                      to="/workspace/$workspaceId"
                      params={{ workspaceId: w.id }}
                    >
                      {m.common_open()}
                    </Link>
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="size-7"
                    onClick={() => copy(w.id)}
                    aria-label={m.common_copy()}
                  >
                    <Copy className="size-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground">
              {m.settings_ws_footer()}
            </p>
          </Card>
        </div>
      </main>
    </>
  )
}
